// src/apps/auth/pages/Login.tsx
import {
    Box,
    Button,
    Container,
    Heading,
    useToast,
    VStack,
    Text,
    Spinner,
    Flex,
    Icon,
    Input,
    InputGroup,
    FormControl,
    FormErrorMessage,
} from '@chakra-ui/react';
import {useParams} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {fetchShopifyInstallUrl, verifyAuthenticatedUser} from "~/store/authSlice";
import { AppDispatch, RootState } from "~/store";
import { useEffect, useState } from "react";
import { FaShopify } from 'react-icons/fa';
import { MdAutoAwesome, MdPhotoCamera, MdPsychology, MdOutlineRocketLaunch } from 'react-icons/md';
import useAuthentication from "~/hooks/useAuthentication.ts";
import useExtendedNavigation from "~/hooks/useExtendedNavigation.ts";
import { Redirect } from '@shopify/app-bridge/actions';
import {createApp} from "@shopify/app-bridge";

const Login = () => {
    const {public_id} = useParams();
    console.log(`LOADED APP ${public_id}`);
    const navigate = useExtendedNavigation();
    const toast = useToast();
    if(!public_id) {
        navigate('/errors/404');
    }
    const dispatch = useDispatch<AppDispatch>();
    const { loading, error } = useSelector(
        (state: RootState) => state.auth
    );
    const {getShopifySession, clearShopifySession, redirectToApplication} = useAuthentication();
    const shopifySession = getShopifySession();


    const [shopUrl, setShopUrl] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [shopUrlError, setShopUrlError] = useState('');

    // Color modes for dark theme
    const bgColor = "gray.900";
    const cardBg = "gray.800";
    const textColor = "gray.100";
    const buttonBg = "blue.500";
    const buttonHoverBg = "blue.600";
    const queryParams = new URLSearchParams(window.location.search);



    useEffect(() => {
        if(shopifySession && public_id) {
            const params = Object.fromEntries(queryParams.entries());
            dispatch(verifyAuthenticatedUser(shopifySession)).unwrap().then(() => {
                redirectToApplication(public_id, params);
                return;
            }).catch(() => {
                clearShopifySession();
            });
        }
        console.log(`PARAMS: ${queryParams}`);
        if (queryParams.has('shop') && public_id && !queryParams.has('code')) {
            const validatedUrl = queryParams.get('shop') as string;
            installApplication(public_id, validatedUrl);
        }

        if(queryParams.has('shop') && public_id && queryParams.has('code')) {

        }
    }, [public_id]);


    useEffect(() => {
        if (error) {
            toast({
                title: 'Authentication Error',
                description: error,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
        }
    }, [error]);

    const validateShopUrl = (url: string) => {
        // Remove any protocol and www
        url = url.replace(/^https?:\/\//, '').replace(/^www\./, '');

        // Check if it's a myshopify.com domain
        if (!url.includes('myshopify.com')) {
            url = `${url}.myshopify.com`;
        }

        // Validate the URL format
        const shopifyUrlRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;
        if (!shopifyUrlRegex.test(url)) {
            return '';
        }

        return url;
    };

    const installApplication =
    async (public_id: string, shop_url: string) => {
        try {
            // remove old state
            localStorage.removeItem('state');
            const response = await dispatch(fetchShopifyInstallUrl({
                public_id: public_id,
                shop: shop_url
            })).unwrap();
            // console.log(response);

            const host = new URLSearchParams(window.location.search).get('host');
            const shop = new URLSearchParams(window.location.search).get('shop');
            const code = new URLSearchParams(window.location.search).get('code');
            localStorage.setItem('state', response.state);

            if (host && shop && response.client_id && !code) {
                const app = createApp({
                    apiKey: response.client_id,
                    host: host,
                    forceRedirect: false
                });

                // Create a redirect action
                const redirect = Redirect.create(app);
                // Perform the redirect
                redirect.dispatch(Redirect.Action.REMOTE, response.auth_url);
            } else if (host && shop && response.client_id && code) {
            } else {
                // Fallback for when App Bridge is not available (e.g., in development)
                window.location.href = response.auth_url;
            }
        } catch (err) {
            setIsValidating(false);
            console.error('Authentication failed:', err);
            toast({
                title: 'Authentication Error',
                description: 'Failed to generate shopify install URL',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
        }
    }

    const handleLoginClick = async () => {
        if(!public_id) {
            return;
        }
        setIsValidating(true);
        setShopUrlError('');
        // if shopUrl ends in / remove it
        if (shopUrl.endsWith('/')) {
            setShopUrl(shopUrl.slice(0, -1));
        }

        const validatedUrl = validateShopUrl(shopUrl);
        if (!validatedUrl) {
            setShopUrlError('Please enter a valid Shopify store URL');
            setIsValidating(false);
            return;
        }

        installApplication(public_id, validatedUrl);
    };

    if (loading) {
        return (
            <Flex
                minH="100vh"
                bg={bgColor}
                alignItems="center"
                justifyContent="center"
            >
                <VStack spacing={6}>
                    <Spinner
                        size="xl"
                        thickness="4px"
                        color="blue.500"
                        emptyColor="gray.700"
                    />
                    <Text color={textColor} fontSize="lg">
                        Connecting to Shopify...
                    </Text>
                </VStack>
            </Flex>
        );
    }

    return (
        <Flex
            minH="100vh"
            bg={bgColor}
            alignItems="center"
            justifyContent="center"
            p={4}
        >
            <Container maxW="lg">
                <VStack spacing={12}>
                    {/* Logo Section */}
                    <VStack spacing={3}>
                        <Icon
                            as={FaShopify}
                            boxSize={16}
                            color="blue.500"
                        />
                        <Heading
                            color={textColor}
                            fontSize={{ base: "3xl", md: "4xl" }}
                            fontWeight="bold"
                            textAlign="center"
                        >
                            Welcome to Cartario
                        </Heading>
                        <Text
                            color="gray.400"
                            fontSize="lg"
                            textAlign="center"
                        >
                            Enhance your product listings with AI-powered optimization and generation
                        </Text>
                    </VStack>

                    {/* Login Card */}
                    <Box
                        w="full"
                        bg={cardBg}
                        p={8}
                        borderRadius="xl"
                        boxShadow="2xl"
                        border="1px"
                        borderColor="gray.700"
                    >
                        <VStack spacing={6}>
                            <VStack spacing={2} w="full">
                                <Text
                                    color={textColor}
                                    fontSize="xl"
                                    fontWeight="medium"
                                >
                                    Get Started
                                </Text>
                                <Text
                                    color="gray.400"
                                    textAlign="center"
                                >
                                    Enter your Shopify store URL to connect
                                </Text>
                            </VStack>

                            <FormControl isInvalid={!!shopUrlError}>
                                <InputGroup size="lg">
                                    <Input
                                        placeholder="your-store.myshopify.com"
                                        value={shopUrl}
                                        onChange={(e) => setShopUrl(e.target.value)}
                                        bg="gray.700"
                                        border="none"
                                        _focus={{
                                            boxShadow: 'none',
                                            bg: 'gray.600'
                                        }}
                                        _hover={{
                                            bg: 'gray.600'
                                        }}
                                        color={textColor}
                                    />
                                </InputGroup>
                                <FormErrorMessage>{shopUrlError}</FormErrorMessage>
                            </FormControl>

                            <Button
                                w="full"
                                size="lg"
                                bg={buttonBg}
                                color="white"
                                _hover={{ bg: buttonHoverBg }}
                                _active={{ bg: buttonHoverBg }}
                                onClick={() => handleLoginClick()}
                                leftIcon={<Icon as={FaShopify} boxSize={5} />}
                                py={7}
                                fontSize="lg"
                                fontWeight="medium"
                                boxShadow="lg"
                                isLoading={loading || isValidating}
                                loadingText="Validating..."
                            >
                                Connect with Shopify
                            </Button>

                            <Text
                                color="gray.500"
                                fontSize="sm"
                                textAlign="center"
                            >
                                By connecting, you agree to our Terms of Service and Privacy Policy
                            </Text>
                        </VStack>
                    </Box>

                    {/* Features Section */}
                    <Flex
                        wrap="wrap"
                        gap={6}
                        justify="center"
                    >
                        {features.map((feature, index) => (
                            <Box
                                key={index}
                                bg={cardBg}
                                p={4}
                                borderRadius="lg"
                                border="1px"
                                borderColor="gray.700"
                                w={{ base: "full", md: "45%" }}
                            >
                                <VStack align="start" spacing={2}>
                                    <Icon
                                        as={feature.icon}
                                        boxSize={5}
                                        color="blue.500"
                                    />
                                    <Text
                                        color={textColor}
                                        fontWeight="medium"
                                    >
                                        {feature.title}
                                    </Text>
                                    <Text color="gray.400" fontSize="sm">
                                        {feature.description}
                                    </Text>
                                </VStack>
                            </Box>
                        ))}
                    </Flex>
                </VStack>
            </Container>
        </Flex>
    );
};

const features = [
    {
        icon: MdAutoAwesome,
        title: "AI-Powered Optimization",
        description: "Automatically enhance your product descriptions and titles for better visibility"
    },
    {
        icon: MdPhotoCamera,
        title: "Product Image Generation",
        description: "Create stunning product images with advanced AI generation tools"
    },
    {
        icon: MdPsychology,
        title: "Custom AI Training",
        description: "Train AI models specific to your product line and brand identity"
    },
    {
        icon: MdOutlineRocketLaunch,
        title: "Performance Boost",
        description: "Improve product listings performance with AI-driven recommendations"
    }
];

export default Login;